import { Injectable } from '@angular/core';
import { YEAR_MONTH_GSI } from '@shared/constants/format-dates.const';

import { PAPERWORKS } from '@shared/constants/paperworks.const';

import format from 'date-fns/format';
import {
  AccessPatterns,
  Paperwork,
  PaperworkDefinition,
  PaperworkReference,
} from '@shared/interfaces/paperwork.interface';
import { PaperworkState } from '@store/reducers';
import { environment } from '@environments/environment';
import { DateService } from '@shared/services/date.service';
import { API_URIS } from '@shared/constants/api-uris.const';
import { RecipeV2 } from '@shared/interfaces/recipe-v2.interface';
import { PCV2Request } from '@shared/interfaces/request.interface';
import { FileToStorage } from '@shared/interfaces/storage.interface';
import { PaperworkV2 } from '@shared/interfaces/paperwork-v2.interface';
import { OrderResponse } from '@shared/interfaces/order-response.interface';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';


const BASE_API: string = environment.baseApiPCv2;
const URI_CLINICAL_ORDERS: string = API_URIS.clinical.orders;

// const RESOURCE_ENDPOINTS = {
//   RMG: 'servicios/v1/ordenes',
//   RMD: 'servicios/v1/ordenes',
//   RMM: 'servicios/v1/ordenes',
// } as const;

@Injectable({
  providedIn: 'root',
})
export class PaperworkService {
  constructor(
    private httpClient: HttpClient,
  ) { }

  async getAccessPatternsPaperwork(paperwork: PaperworkReference): Promise<AccessPatterns> {
    const { unixtime, datetime } = await DateService.getTime();
    const control = Math.floor(Math.random() * 999);
    const paperworkApp = PAPERWORKS.find((app) => app.module === paperwork.module);
    const yearMonth = format(new Date(datetime), YEAR_MONTH_GSI);
    const prefix = paperworkApp.prefix.toUpperCase();
    const professionalId = paperwork.professionalId;
    const suffix = paperwork.submodule.suffix.toUpperCase();
    const shortId = suffix + unixtime + control;
    const gsiMultiCondition2Nd = shortId;
    const gsiMultiCondition1St = `${prefix}#DTE#${yearMonth}#PRO#${professionalId}#${suffix}`;

    return { prefix, suffix, shortId, datetime, gsiMultiCondition2Nd, gsiMultiCondition1St };
  }

  clearRecordForSave(paperworkState): { paperwork: Paperwork; definition: PaperworkDefinition } {
    const { paperwork, definition } = paperworkState;
    const professional = { ...paperwork.professional, base64: undefined };
    return { paperwork: { ...paperwork, professional }, definition };
  }

  isSignatureRequired(paperworkState: PaperworkState): boolean {
    const { isSigned } = paperworkState.definition.submodule.workflow;
    return isSigned;
  }

  getFilesToStore(paperworkState: PaperworkState): FileToStorage[] {
    const { isStored } = paperworkState.definition.submodule.workflow;
    const { files } = paperworkState;

    if (isStored || files.length) return files;
    return [];
  }

  isStorageRequired(paperworkState: PaperworkState): boolean {
    const { isStored } = paperworkState.definition.submodule.workflow;
    return isStored;
  }

  isPutRequired(paperworkState: PaperworkState): boolean {
    const { isSaving } = paperworkState.definition.submodule.workflow;
    return isSaving;
  }

  isRequiredSendEmail(paperworkState: PaperworkState): boolean {
    const { notifications } = paperworkState?.definition?.submodule?.workflow || {};
    return notifications?.length ? true : false;
  }

  isRequiredNextControl(paperworkState: PaperworkState) {
    const { hasNextControl } = paperworkState.definition.submodule.workflow;
    return hasNextControl;
  }

  /**
   * Servicio para gestionar trámites (paperworks) en la aplicación.
   * Proporciona métodos para obtener patrones de acceso, limpiar registros,
   * verificar requisitos de firma, almacenamiento y envío de correo,
   * adaptar el estado del trámite para formularios y enviar recursos al backend.
   */
  public adaptForm(paperworkState: PaperworkState): PaperworkV2<RecipeV2[]> {
    const { paperwork } = paperworkState;
    const professionalName = `${paperwork.professional.names} ${paperwork.professional.surnames}`;

    return {
      professional: {
        id: paperwork.professional.id,
        display: professionalName,
        keys: {
          documentNumber: paperwork.professional.rut,
          specialty: {
            id: paperwork.professional.specialty.id,
            display: paperwork.professional.specialty.name,
          },
          email: paperwork.professional.email,
        },
      },
      patient: {
        id: paperwork.patient.id,
        display: `${paperwork.patient.names} ${paperwork.patient.surnames}`,
        keys: {
          birthDate: DateService.formatDateToDDMMYYYY((paperwork.patient.birth.toISOString()).split('T')[0]),
          documentNumber: paperwork.patient.documentNumber,
          phoneNumber: paperwork.patient.phone,
          address: paperwork.patient.address.street ? `${paperwork.patient.address.street} ${paperwork.patient.address.streetNumber || ''}`.trim() : '',
          email: paperworkState.paperwork.patient.email
        }
      },
      creator: {
        id: paperwork.professional.id,
        display: professionalName,
      },
      scope: paperworkState.definition.submodule.suffix,
      action: {
        isPreview: false,
        hasQuotation: true
      },
      content: paperwork.content.map(content => {
        return {
          ...content,
          startTreatment: this.formatDateToISOUTC(content.startTreatment)
        }

      }),
      version: environment.version,
    };
  }

  /**
   * Envía una solicitud POST para actualizar un recurso específico en el backend.
   *
   * @param body El cuerpo de la solicitud, que contiene los datos del trámite y su contenido.
   * @param resourceType El tipo de recurso que se va a actualizar.
   * @returns Un observable que emite la respuesta de la orden actualizada.
   */
  putResource(body: PaperworkV2<RecipeV2[]>): Observable<OrderResponse> {
    return this.httpClient.post<PCV2Request>(`${BASE_API}/${URI_CLINICAL_ORDERS}`, body).pipe(
      map(({ data }: { data: OrderResponse }) => data),
    );
  }

  /**
   * Convierte una fecha a formato ISO UTC.
   */
  private formatDateToISOUTC(dateString: string): string {
    if (!dateString) {
      return new Date().toISOString();
    }

    // Si ya está en formato ISO UTC (contiene 'T'), devolverlo tal cual
    if (dateString.includes('T')) {
      return dateString;
    }

    // Si es formato YYYY-MM-DD, agregar la hora UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString + 'T00:00:00.000Z';
    }

    // Si no coincide con ningún formato conocido, convertir a ISO UTC
    return new Date(dateString).toISOString();
  }
}
